// Projects' new task form, with its sprint (decision 66: "the Projects sprint-task form" moves to the
// v2 kit in Block J). The Add task modal's pattern (TaskForm.js, UI-5): the kit's Modal with the
// highlight band, the underlined title, Priority as a segmented control, "Assign to" through the
// same AssignPicker, and a sticky footer.
//
// Block J fixes:
//   - It closed the moment Create was pressed, before the server answered, so a refused task lost
//     everything typed. It now waits; a refusal keeps the form open with the server's words.
//   - "Create new project" here failed in silence (console only); it now says why, beside it.
//   - alert() for a missing title or project is now said beside the field.
import React, { useState } from 'react';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useWorkspaceRoster } from '../../hooks/useWorkspaceRoster';
import projectManager from '../../models/projects';
import { messageFromError } from '../../utils/apiError';
import { Button, DateInput, Field, Input, Modal, SegmentedControl, Select, Textarea, TitleInput } from '../../ui';
import AssignPicker from '../task/AssignPicker';
import '../task/taskform.css';
import '../Projects/projects.css';

const PRIORITY_CHOICES = [
  { value: 'Low', label: 'Low', color: 'priority-low' },
  { value: 'Medium', label: 'Medium', color: 'priority-medium' },
  { value: 'High', label: 'High', color: 'priority-high', dot: true },   // decision 50: High has the dot
];
const NEW_PROJECT = '__new__';

function CreateTaskModal({ isOpen, onClose, onSave, projectId = null, sprintId = null }) {
  const { currentUser } = useCurrentUser();

  const blank = () => ({
    title:      '',
    description:'',
    projectId:  projectId ? String(projectId) : '',
    sprintId:   sprintId ? String(sprintId) : '',
    assignedTo: currentUser?.id ? [currentUser.id] : [],
    dueDate:    '',
    priority:   'Medium',
    status:     'todo',
    storyPoints: 5,
    tags:       [],
  });
  const [formData, setFormData] = useState(blank);
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState('');
  const [saving, setSaving] = useState(false);

  const [tagInput,        setTagInput]        = useState('');
  const [showNewProject,  setShowNewProject]  = useState(false);
  const [newProjectName,  setNewProjectName]  = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [projectFailure,  setProjectFailure]  = useState('');

  const [projects, setProjects] = useState(() => projectManager.getAllProjects());
  const { members: users } = useWorkspaceRoster();
  // Opened from inside a project, that project is the answer: this form stays mounted while closed, so
  // the project it first saw (none, on the landing) used to stick, and adding a task from a project
  // then said "Please select a project" with no project field on screen.
  const chosenProject = projectId ? String(projectId) : formData.projectId;
  const sprints = chosenProject ? projectManager.getSprintsByProject(parseInt(chosenProject, 10)) : [];

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      set('tags', [...formData.tags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => set('tags', formData.tags.filter(t => t !== tag));

  // ── Create new project inline ─────────────────────────────────────────────

  const handleCreateProject = async () => {
    const name = newProjectName.trim();
    if (!name) return;
    setCreatingProject(true);
    setProjectFailure('');
    try {
      const created = await projectManager.createProject({ name, status: 'active' });
      const refreshed = projectManager.getAllProjects();
      setProjects(refreshed);
      const newId = created?.id || refreshed.find(p => p.name === name)?.id;
      if (newId) {
        set('projectId', String(newId));
        set('sprintId', '');
      }
      setShowNewProject(false);
      setNewProjectName('');
    } catch (err) {
      setProjectFailure(messageFromError(err, 'That project could not be created.'));
    } finally {
      setCreatingProject(false);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const close = () => {
    if (saving) return;
    setErrors({});
    setFailure('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const errs = {};
    if (!formData.title.trim()) errs.title = 'Give the task a title.';
    if (!chosenProject) errs.project = 'Choose a project.';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    setFailure('');
    try {
      await onSave({
        ...formData,
        title:       formData.title.trim(),
        projectId:   parseInt(chosenProject, 10),
        sprintId:    formData.sprintId ? parseInt(formData.sprintId, 10) : null,
        storyPoints: parseInt(formData.storyPoints, 10) || 0,
        createdBy:   currentUser?.id || null,
      });
    } catch (err) {
      // Stays open, with what was typed, and the reason.
      setFailure(messageFromError(err, 'That task was not added.'));
      setSaving(false);
      return;
    }
    setSaving(false);
    setFormData(blank());
    setTagInput('');
    onClose();
  };

  if (!isOpen) return null;

  const footer = (
    <div className="pj-foot">
      {failure && <p className="pj-failed" role="alert">{failure}</p>}
      <Button variant="tertiary" onClick={close} disabled={saving}>Cancel</Button>
      <Button variant="confirm" type="submit" form="project-task-form" disabled={saving} aria-busy={saving || undefined}>
        {saving ? 'Creating…' : 'Create task'}
      </Button>
    </div>
  );

  return (
    <Modal open onClose={close} title="Create new task" color="highlight" width={640} footer={footer}>
      <form id="project-task-form" className="ui-taskform" onSubmit={handleSubmit} noValidate>
        <div className="ui-taskform__top">
          <TitleInput data-autofocus value={formData.title} placeholder="Enter task title…" aria-label="Task title"
            aria-invalid={errors.title ? true : undefined}
            onChange={e => { set('title', e.target.value); if (errors.title) setErrors(x => ({ ...x, title: null })); }} />
          {errors.title && <p className="ui-field__error">{errors.title}</p>}
          <div className="ui-taskform__typerow">
            <div className="ui-taskform__inline ui-taskform__priority">
              <span className="ui-taskform__eyebrow" aria-hidden="true">Priority</span>
              <SegmentedControl label="Priority" options={PRIORITY_CHOICES} value={formData.priority} onChange={(p) => set('priority', p)} />
            </div>
          </div>
        </div>

        <Field label="Description">
          <Textarea rows={3} value={formData.description} onChange={e => set('description', e.target.value)} placeholder="Describe the task…" />
        </Field>

        {/* Block 213: the project is asked for ONLY when the form was not opened from inside a
            project; from a project it is already chosen. */}
        <div className="pj-pair">
          {!projectId && (
            <div className="pj-drawer__section">
              <Field label="Project" required error={errors.project}>
                <Select value={showNewProject ? NEW_PROJECT : formData.projectId}
                  onChange={e => {
                    if (e.target.value === NEW_PROJECT) {
                      setShowNewProject(true);
                    } else {
                      set('projectId', e.target.value);
                      set('sprintId', '');
                      setShowNewProject(false);
                      if (errors.project) setErrors(x => ({ ...x, project: null }));
                    }
                  }}
                  options={[
                    { value: '', label: 'Select project…' },
                    ...projects.map(p => ({ value: String(p.id), label: p.name })),
                    { value: NEW_PROJECT, label: '+ Create new project…' },
                  ]} />
              </Field>
              {showNewProject && (
                <div className="pj-new-project">
                  <Input autoFocus aria-label="New project name" value={newProjectName} placeholder="Project name…"
                    onChange={e => setNewProjectName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); handleCreateProject(); }
                      if (e.key === 'Escape') { setShowNewProject(false); set('projectId', ''); }
                    }} />
                  {projectFailure && <p className="pj-failed" role="alert">{projectFailure}</p>}
                  <div className="pj-row">
                    <Button size="sm" variant="confirm" onClick={handleCreateProject} disabled={!newProjectName.trim() || creatingProject}>
                      {creatingProject ? 'Creating…' : 'Create project'}
                    </Button>
                    <Button size="sm" variant="tertiary" onClick={() => { setShowNewProject(false); set('projectId', ''); setNewProjectName(''); setProjectFailure(''); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <Field label="Sprint (optional)">
            <Select value={formData.sprintId} onChange={e => set('sprintId', e.target.value)} disabled={!chosenProject || showNewProject}
              options={[{ value: '', label: 'No sprint' }, ...sprints.map(s => ({ value: String(s.id), label: s.name }))]} />
          </Field>
        </div>

        <section className="ui-taskform__group" aria-labelledby="project-task-assign">
          <span id="project-task-assign" className="ui-taskform__eyebrow">Assign to</span>
          <AssignPicker members={users} value={formData.assignedTo} currentUserId={currentUser?.id}
            onChange={(ids) => set('assignedTo', ids)} />
        </section>

        <div className="pj-pair">
          <Field label="Due date">
            <DateInput value={formData.dueDate} onChange={e => set('dueDate', e.target.value)} />
          </Field>
          <Field label="Story points">
            <Input type="number" min="1" max="50" inputMode="numeric" value={formData.storyPoints} onChange={e => set('storyPoints', e.target.value)} />
          </Field>
        </div>

        <div className="pj-drawer__section">
          <span className="pj-drawer__label">Tags</span>
          {formData.tags.length > 0 && (
            <div className="pj-chips">
              {formData.tags.map(tag => (
                <span key={tag} className="pj-tag">{tag}<button type="button" aria-label={`Remove tag ${tag}`} onClick={() => removeTag(tag)}>×</button></span>
              ))}
            </div>
          )}
          <div className="pj-inline">
            <Input aria-label="New tag" value={tagInput} placeholder="Add a tag…"
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
            <Button variant="secondary" onClick={addTag} disabled={!tagInput.trim()}>Add</Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default CreateTaskModal;
